import * as Select from '@radix-ui/react-select';
import {type JSX, type CSSProperties} from 'react';
import {clsx} from 'clsx';
import {CheckIcon, PulldownArrowIcon} from '../icons';
import styles from './pulldown.module.css';

type Props<T extends string> = {
  readonly id: string;
  readonly options: Array<{
    key: string;
    value: string;
    Icon: () => JSX.Element;
  }>;
  readonly value: T;
  readonly onValueChange: (newValue: T) => void;
  readonly align: 'start' | 'end';
  readonly ariaLabel?: string;
  readonly style?: CSSProperties;
};

export function Pulldown<T extends string>({
  id,
  value,
  onValueChange,
  options,
  align,
  ariaLabel,
  ...props
}: Props<T>) {
  const selected = options.find((option) => option.value === value)?.key;

  return (
    <Select.Root value={value} onValueChange={onValueChange}>
      <Select.Trigger
        aria-label={ariaLabel}
        id={id}
        className={clsx(styles.trigger, styles.value)}
        {...props}
      >
        <Select.Value asChild>
          <span>{selected}</span>
        </Select.Value>
        <Select.Icon asChild>
          <PulldownArrowIcon />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position='popper'
          align={align}
          sideOffset={4}
          className={styles.content}
        >
          <Select.Viewport>
            {options.map((option) => (
              <Select.Item
                key={option.key}
                value={option.value}
                className={styles.item}
              >
                <Select.ItemText asChild>
                  <span className={clsx(styles.itemText, styles.value)}>
                    <option.Icon />
                    {option.key}
                  </span>
                </Select.ItemText>
                <Select.ItemIndicator>
                  <CheckIcon />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
